import { useCallback, useEffect, useRef, useState } from "react";
import * as O from "@/components/Ornaments";
import { useSound } from "@/lib/sound";
import { useT } from "@/lib/i18n";
import {
  GuestPhoto,
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  GuestbookSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import type { VariantProps } from "./types";

// ═══════════════════════════════════════════════════════════════════════
// SEASIDE — Marvarid (w07)
//
// Coastal / destination wedding at Charvak reservoir. Pearl + sand +
// sea-foam palette, watercolor seascape, Tenor Sans typography.
//
// Signature element:
//   * The hero name plate sits over a full-width sea-horizon photo, with
//     an animated SVG wave (translateX keyframe, 6s ease-in-out infinite)
//     gently rolling beneath the names. Reduced-motion shows the wave
//     but freezes the drift.
//
// Unique-to-Marvarid section between Story and Gallery:
//   * "Tide chart" card — a watercolor wave SVG with the wedding-day tide
//     times. Pure decoration, sets the destination feel.
//
// Sound budget (≤ 4 triggers):
//   1. RSVP "yes"  → chime
//   2. RSVP "no"   → tap
//   3. Section reveal (first hit) → swoosh
//   4. Hero "save the date" CTA → tap
// ═══════════════════════════════════════════════════════════════════════

// ─── prefers-reduced-motion (unsubscribed-safe) ───────────────────────
function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e: MediaQueryListEvent) => setReduce(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return reduce;
}

// ─── Reveal-on-scroll, gated by reduced-motion ────────────────────────
interface RevealProps {
  children: React.ReactNode;
  delay?: number;
  reduce: boolean;
  onFirstReveal?: () => void;
}

function Reveal({ children, delay = 0, reduce, onFirstReveal }: RevealProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(reduce);
  const firedRef = useRef(false);

  useEffect(() => {
    if (reduce) {
      setShown(true);
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            if (!firedRef.current) {
              firedRef.current = true;
              onFirstReveal?.();
            }
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduce, onFirstReveal]);

  return (
    <div
      ref={ref}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(22px)",
        transition: reduce ? "none" : `opacity 1s ease-out ${delay}ms, transform 1s ease-out ${delay}ms`,
        willChange: reduce ? undefined : "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

// ─── Tide chart — the unique Marvarid section ─────────────────────────
interface TideChartProps {
  accent: string;
  ink: string;
  bg: string;
  font: string;
  reduce: boolean;
}

function TideChart({ accent, ink, bg, font, reduce }: TideChartProps) {
  const tt = useT();
  // Plausible mid-July Charvak reservoir-day "tide" times — purely
  // decorative (Charvak is freshwater so true tides ≈ 0; this is the
  // mood-piece for the destination feel).
  const items: Array<[string, string, "high" | "low"]> = [
    ["06:42", tt("variant.w07.tide.0.label"), "low"],
    ["12:14", tt("variant.w07.tide.1.label"), "high"],
    ["17:55", tt("variant.w07.tide.2.label"), "high"],
    ["21:08", tt("variant.w07.tide.3.label"), "low"],
  ];
  return (
    <section
      className="marvarid-tide"
      style={{
        padding: "100px 32px",
        borderTop: `1px solid ${ink}22`,
        textAlign: "center",
        position: "relative",
        background: `linear-gradient(180deg, ${bg} 0%, ${accent}0e 100%)`,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.4em",
          color: accent,
          marginBottom: 16,
        }}
      >
        TIDE · 18.07.2026
      </div>
      <h2
        style={{
          fontFamily: font,
          fontSize: "clamp(32px, 4vw, 44px)",
          fontWeight: 300,
          letterSpacing: "0.06em",
          marginBottom: 8,
          color: ink,
        }}
      >
        {tt("variant.w07.tide.title")}
      </h2>
      <p
        style={{
          fontFamily: "var(--font-body)",
          fontSize: 15,
          fontStyle: "italic",
          color: `${ink}99`,
          marginBottom: 48,
          maxWidth: 480,
          margin: "0 auto 48px",
        }}
      >
        {tt("variant.w07.tide.subtitle")}
      </p>

      {/* Wave + tide markers */}
      <div
        aria-hidden
        style={{
          maxWidth: 720,
          margin: "0 auto 32px",
          position: "relative",
          height: 120,
        }}
      >
        <svg viewBox="0 0 720 120" width="100%" height="120" preserveAspectRatio="none">
          <defs>
            <linearGradient id="marvarid-tide-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor={accent} stopOpacity="0.0" />
              <stop offset="100%" stopColor={accent} stopOpacity="0.32" />
            </linearGradient>
          </defs>
          {/* Wavy curve representing tide oscillation across the day */}
          <path
            d="M 0 80 Q 90 40 180 70 T 360 60 T 540 50 T 720 70 L 720 120 L 0 120 Z"
            fill="url(#marvarid-tide-fill)"
          />
          <path
            d="M 0 80 Q 90 40 180 70 T 360 60 T 540 50 T 720 70"
            fill="none"
            stroke={accent}
            strokeWidth="1.4"
            strokeLinecap="round"
            style={{
              animation: reduce ? "none" : "marvaridTideShift 9s ease-in-out infinite",
              transformOrigin: "center",
            }}
          />
          {/* Tide markers */}
          {items.map(([time, , kind], i) => {
            const x = 90 + i * (540 / 3);
            const y = kind === "high" ? 50 : 78;
            return (
              <g key={i}>
                <line x1={x} y1={y} x2={x} y2={108} stroke={accent} strokeOpacity="0.5" strokeWidth="0.6" strokeDasharray="2 3" />
                <circle cx={x} cy={y} r="4.5" fill={bg} stroke={accent} strokeWidth="1.2" />
                <text
                  x={x}
                  y={y - 8}
                  fontFamily="var(--font-ui)"
                  fontSize="9"
                  fontWeight={700}
                  letterSpacing="0.12em"
                  textAnchor="middle"
                  fill={ink}
                >
                  {time}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      <div
        className="marvarid-tide-grid"
        style={{
          maxWidth: 720,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 16,
        }}
      >
        {items.map(([time, label], i) => (
          <div
            key={i}
            style={{
              padding: "18px 12px",
              border: `1px solid ${accent}55`,
              background: bg,
            }}
          >
            <div
              style={{
                fontFamily: font,
                fontSize: 22,
                fontWeight: 300,
                letterSpacing: "0.06em",
                color: accent,
                marginBottom: 6,
              }}
            >
              {time}
            </div>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.16em",
                color: `${ink}99`,
                textTransform: "uppercase",
              }}
            >
              {label}
            </div>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes marvaridTideShift {
          0%, 100% { transform: translateX(0); }
          50%      { transform: translateX(-8px); }
        }
        @media (max-width: 640px) {
          .marvarid-tide-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>
    </section>
  );
}

// ─── Hero wave (signature element) ────────────────────────────────────
interface HeroWaveProps {
  accent: string;
  reduce: boolean;
}

function HeroWave({ accent, reduce }: HeroWaveProps) {
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 64,
        height: 60,
        pointerEvents: "none",
      }}
    >
      <div
        className="marvarid-hero-wave-track"
        style={{
          width: "120%",
          marginLeft: "-10%",
          height: "100%",
          animation: reduce ? "none" : "marvaridHeroWave 6s ease-in-out infinite",
          willChange: reduce ? undefined : "transform",
        }}
      >
        <svg viewBox="0 0 1200 60" width="100%" height="100%" preserveAspectRatio="none">
          <path
            d="M 0 30 Q 100 6 200 30 T 400 30 T 600 30 T 800 30 T 1000 30 T 1200 30"
            fill="none"
            stroke="rgba(255,255,255,0.85)"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
          <path
            d="M 0 42 Q 100 22 200 42 T 400 42 T 600 42 T 800 42 T 1000 42 T 1200 42"
            fill="none"
            stroke={accent}
            strokeOpacity="0.85"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
          <path
            d="M 0 54 Q 100 38 200 54 T 400 54 T 600 54 T 800 54 T 1000 54 T 1200 54"
            fill="none"
            stroke="rgba(255,255,255,0.55)"
            strokeWidth="1"
            strokeLinecap="round"
          />
        </svg>
      </div>
      <style>{`
        @keyframes marvaridHeroWave {
          0%   { transform: translateX(0); }
          50%  { transform: translateX(-5%); }
          100% { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// VARIANT
// ═══════════════════════════════════════════════════════════════════════
export function SeasideVariant({ t, isDark, ctx }: VariantProps) {
  const tt = useT();
  const photos = t.photos || [];
  const reduce = usePrefersReducedMotion();
  const { play } = useSound();

  // Sound: RSVP yes → chime, RSVP no → tap
  const baseSetRsvp = ctx.rsvp.setRsvp;
  const setRsvpWithSound = useCallback(
    (v: string | null) => {
      if (v === "yes") play("chime");
      else if (v === "no") play("tap");
      baseSetRsvp(v);
    },
    [baseSetRsvp, play],
  );

  // Sound: section reveal swoosh — only fire once for the first reveal so
  // the page doesn't sound like a wave-machine.
  const firstSwooshRef = useRef(false);
  const onFirstReveal = useCallback(() => {
    if (firstSwooshRef.current) return;
    firstSwooshRef.current = true;
    play("swoosh");
  }, [play]);

  // Soft handwritten "save the date" callout (animated stroke draw).
  const [savedTouched, setSavedTouched] = useState(false);

  const heroSrc = photos[0];

  // Split couple names for ampersand styling
  const [n1, n2] = t.names.split("&").map((s) => s.trim());

  return (
    <div className="marvarid-variant" style={{ background: t.bg, color: t.ink }}>
      {/* ─── HERO ────────────────────────────────────────────────── */}
      <section
        className="marvarid-hero"
        style={{
          position: "relative",
          minHeight: "100vh",
          padding: "0",
          overflow: "hidden",
          color: "#FFFFFF",
        }}
      >
        {/* Full-bleed sea-horizon photo */}
        <GuestPhoto
          src={heroSrc}
          fallbackBg={t.bg}
          fallbackTone={t.accent}
          style={{ position: "absolute", inset: 0 }}
        />

        {/* Sea-foam tint + bottom darkening for legibility */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background: `
              linear-gradient(180deg, rgba(42,58,58,0.10) 0%, rgba(42,58,58,0) 35%, rgba(42,58,58,0.55) 100%),
              radial-gradient(120% 80% at 50% 30%, ${t.accent}26 0%, ${t.accent}00 55%)
            `,
          }}
        />

        {/* Top eyebrow band */}
        <div
          style={{
            position: "absolute",
            top: 32,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 14,
            color: "rgba(255,255,255,0.92)",
          }}
        >
          <span style={{ width: 28, height: 1, background: "rgba(255,255,255,0.6)" }} />
          <span
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.45em",
            }}
          >
            BY THE WATER · CHARVAK · 2026
          </span>
          <span style={{ width: 28, height: 1, background: "rgba(255,255,255,0.6)" }} />
        </div>

        {/* Centered name plate */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: "0 24px",
          }}
        >
          {/* Pearl trio */}
          <div
            aria-hidden
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              marginBottom: 28,
              opacity: 0,
              animation: reduce ? "none" : "marvaridFadeUp 1.2s ease-out 0.2s forwards",
            }}
          >
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                style={{
                  width: i === 1 ? 10 : 7,
                  height: i === 1 ? 10 : 7,
                  borderRadius: "50%",
                  background: `radial-gradient(circle at 35% 35%, #FFFFFF 0%, ${t.bg} 60%, ${t.accent} 100%)`,
                  boxShadow: `0 0 6px rgba(255,255,255,0.45)`,
                }}
              />
            ))}
          </div>

          <div
            className="marvarid-handwritten"
            style={{
              fontFamily: "var(--font-hand)",
              fontSize: "clamp(20px, 2.6vw, 28px)",
              color: "rgba(255,255,255,0.92)",
              marginBottom: 20,
              opacity: 0,
              animation: reduce ? "none" : "marvaridFadeUp 1.2s ease-out 0.4s forwards",
            }}
          >
            save the date
          </div>

          <h1
            className="marvarid-couple"
            style={{
              fontFamily: t.font,
              fontSize: "clamp(54px, 9vw, 108px)",
              fontWeight: 300,
              lineHeight: 0.95,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              margin: 0,
              color: "#FFFFFF",
              textShadow: "0 1px 24px rgba(20,32,32,0.45)",
              opacity: 0,
              animation: reduce ? "none" : "marvaridFadeUp 1.4s ease-out 0.6s forwards",
            }}
          >
            {n1}
            <span
              style={{
                display: "block",
                fontFamily: "var(--font-hand)",
                fontStyle: "italic",
                fontSize: "0.36em",
                letterSpacing: 0,
                fontWeight: 400,
                color: t.accent,
                margin: "0.2em 0 0.15em",
                textTransform: "none",
              }}
            >
              and
            </span>
            {n2}
          </h1>

          {/* Sea-foam underline (animated draw) */}
          <svg
            width="180"
            height="14"
            viewBox="0 0 200 14"
            style={{
              marginTop: 28,
              opacity: 0,
              animation: reduce ? "none" : "marvaridFadeUp 1s ease-out 0.95s forwards",
            }}
            aria-hidden
          >
            <path
              d="M 4 8 Q 30 2 60 8 T 120 8 T 196 6"
              stroke={t.accent}
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
            />
          </svg>

          <div
            style={{
              marginTop: 26,
              fontFamily: "var(--font-ui)",
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: "0.36em",
              color: "rgba(255,255,255,0.95)",
              opacity: 0,
              animation: reduce ? "none" : "marvaridFadeUp 1s ease-out 1.1s forwards",
            }}
          >
            {t.date} · {t.venue.toUpperCase()}
          </div>

          <button
            type="button"
            onClick={() => {
              play("tap");
              setSavedTouched(true);
              const el = document.getElementById("marvarid-rsvp");
              if (el && typeof el.scrollIntoView === "function") {
                el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
              }
            }}
            style={{
              marginTop: 36,
              padding: "14px 28px",
              background: savedTouched ? t.accent : "transparent",
              border: "1px solid rgba(255,255,255,0.85)",
              color: savedTouched ? "#FFFFFF" : "rgba(255,255,255,0.95)",
              fontFamily: "var(--font-ui)",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.32em",
              cursor: "pointer",
              backdropFilter: "blur(2px)",
              opacity: 0,
              animation: reduce ? "none" : "marvaridFadeUp 1s ease-out 1.3s forwards",
              transition: "background 0.25s ease, color 0.25s ease",
            }}
            aria-label={tt("variant.w07.hero.cta")}
          >
            {tt("variant.w07.hero.cta")}
          </button>
        </div>

        {/* SIGNATURE: animated wave under the names */}
        <HeroWave accent={t.accent} reduce={reduce} />

        {/* Scroll cue */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            bottom: 18,
            color: "rgba(255,255,255,0.7)",
            fontFamily: "var(--font-ui)",
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.4em",
          }}
        >
          ↓ SCROLL
        </div>

        <style>{`
          @keyframes marvaridFadeUp {
            from { opacity: 0; transform: translateY(14px); }
            to   { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </section>

      {/* ─── COUNTDOWN ──────────────────────────────────────────── */}
      <Reveal reduce={reduce} onFirstReveal={onFirstReveal}>
        <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />
      </Reveal>

      {/* ─── STORY ──────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <StorySection
          t={t}
          eyebrow={tt("variant.w07.story.eyebrow")}
          title={tt("variant.w07.story.title")}
          body={tt("variant.w07.story.body")}
        />
      </Reveal>

      {/* ─── TIDE CHART (unique to Marvarid) ────────────────────── */}
      <Reveal reduce={reduce}>
        <TideChart accent={t.accent} ink={t.ink} bg={t.bg} font={t.font} reduce={reduce} />
      </Reveal>

      {/* ─── GALLERY ────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <GallerySection t={t} />
      </Reveal>

      {/* ─── SCHEDULE ───────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <ScheduleSection
          t={t}
          items={[
            ["10:00", tt("variant.w07.schedule.0.event")],
            ["12:00", tt("variant.w07.schedule.1.event")],
            ["14:00", tt("variant.w07.schedule.2.event")],
            ["17:00", tt("variant.w07.schedule.3.event")],
            ["20:00", tt("variant.w07.schedule.4.event")],
            ["22:30", tt("variant.w07.schedule.5.event")],
          ]}
        />
      </Reveal>

      {/* ─── MAP ────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <MapSection
          t={t}
          isDark={isDark}
          addressLine={tt("variant.w07.map.address")}
        />
      </Reveal>

      {/* ─── RSVP ───────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <div id="marvarid-rsvp">
          <RsvpSection
            t={t}
            isDark={isDark}
            rsvp={ctx.rsvp.rsvp}
            setRsvp={setRsvpWithSound}
            guests={ctx.rsvp.guests}
            setGuests={ctx.rsvp.setGuests}
            guestName={ctx.rsvp.guestName}
            setGuestName={ctx.rsvp.setGuestName}
            slug={ctx.slug}
          />
        </div>
      </Reveal>

      {/* ─── GUESTBOOK ──────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <GuestbookSection
          t={t}
          isDark={isDark}
          wishes={ctx.guestbook.wishes}
          setWishes={ctx.guestbook.setWishes}
          guestName={ctx.guestbook.guestName}
          setGuestName={ctx.guestbook.setGuestName}
          wish={ctx.guestbook.wish}
          setWish={ctx.guestbook.setWish}
          slug={ctx.slug}
        />
      </Reveal>

      {/* ─── SHARE ──────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <ShareSection t={t} />
      </Reveal>

      {/* ─── FOOTER ─────────────────────────────────────────────── */}
      <div
        aria-hidden
        style={{
          padding: "24px 0 0",
          textAlign: "center",
          color: t.accent,
          opacity: 0.7,
        }}
      >
        <O.Flourish width={140} color={t.accent} />
      </div>
      <GuestFooter t={t} />

      {/* ─── Mobile refinements ─────────────────────────────────── */}
      <style>{`
        @media (max-width: 768px) {
          .marvarid-variant .marvarid-hero {
            min-height: min(92vh, 700px);
            padding: 64px 18px 48px !important;
          }
          .marvarid-variant .marvarid-couple {
            font-size: clamp(36px, 12vw, 64px) !important;
            letter-spacing: 0.06em !important;
          }
          .marvarid-variant .marvarid-tide { padding: 64px 18px !important; }
          .marvarid-variant .marvarid-tide-grid {
            grid-template-columns: 1fr !important;
          }
          .marvarid-variant button { min-height: 44px; }
          /* Prevent the 120%-wide wave track from causing horizontal overflow */
          .marvarid-variant .marvarid-hero-wave-track {
            width: 100% !important;
            margin-left: 0 !important;
          }
          /* Ensure hero CTA is always visible even when keyframe is suppressed
             by the global mobile animation kill */
          .marvarid-variant .marvarid-hero button {
            opacity: 1 !important;
          }
        }
      `}</style>
    </div>
  );
}
